// SalesPage.jsx - Version avec tableau responsive
import React, { useEffect, useState, useMemo } from "react";
import salesStore from "../../store/salesStore";
import { useServiceStore } from "../../store/serviceStore";
import userManagementStore from "../../store/userManagementStore";
import {
  BiCalendar,
  BiPlus,
  BiReset,
  BiSearch,
  BiReceipt,
  BiUser,
  BiChevronLeft,
  BiChevronRight,
  BiDetail,
  BiPhone,
  BiShoppingBag,
} from "react-icons/bi";
import { toast } from "react-toastify";
import { FaEnvelope, FaWhatsapp } from "react-icons/fa";
import {
  getClientContact,
  getClientName,
  getWhatsappLink,
  matchesClientSearch,
} from "../../utils/clientInfo";

// Composant Modal de Détails
function PaymentDetailModal({ isOpen, onClose, payment }) {
  if (!isOpen || !payment) return null;

  const client = getClientContact(payment);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="font-poppins font-semibold text-xl text-title">
              Détails de la vente #{payment.code}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 flex items-center gap-1">
                <BiShoppingBag />
                Vente en boutique
              </span>
              <span
                className={`px-3 py-1 text-sm font-medium ${
                  payment.method === "CASH"
                    ? "bg-green-100 text-green-800"
                    : payment.method === "MOBILE_MONEY"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-secondary-100 text-secondary-800"
                }`}
              >
                {payment.method}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Client
              </h3>
              {client ? (
                <div className="space-y-3">
                  <div>
                    <p className="font-nunito text-sm text-text">Nom complet</p>
                    <p className="font-poppins font-medium">
                      {client.name || "Client non renseigné"}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">Contact</p>
                    <div className="flex gap-3 mt-2">
                      {client.email && (
                        <a
                          href={`mailto:${client.email}`}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          <FaEnvelope size={20} />
                        </a>
                      )}
                      {client.telephone && (
                        <a
                          href={`tel:${client.telephone}`}
                          className="text-secondary-600 hover:text-secondary-800"
                        >
                          <BiPhone size={20} />
                        </a>
                      )}
                      {client.whatsapp && (
                        <a
                          href={getWhatsappLink(client)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                        >
                          <FaWhatsapp size={20} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="font-nunito text-text">Client non enregistré</p>
              )}
            </div>

            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Paiement
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="font-nunito text-sm text-text">Montant</p>
                  <p className="font-poppins font-bold text-xl text-montant">
                    {parseFloat(payment.amount).toLocaleString()} FCFA
                  </p>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">Date</p>
                  <p className="font-poppins font-medium">
                    {new Date(payment.created_at).toLocaleString("fr-FR")}
                  </p>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">Statut</p>
                  <span
                    className={`px-2 py-1 text-sm ${
                      payment.status === "COMPLETED"
                        ? "bg-green-100 text-green-800"
                        : payment.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                    }`}
                  >
                    {payment.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Services ({payment.services.length})
              </h3>
              <div className="space-y-3">
                {payment.services.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white border"
                  >
                    <div>
                      <p className="font-poppins font-medium">
                        {item.service?.nom || "Service"}
                      </p>
                      <p className="font-nunito text-sm text-text">
                        {item.service?.description || ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-poppins font-bold text-montant">
                        {parseFloat(item.unitPrice).toLocaleString()} FCFA
                      </p>
                      <p className="font-nunito text-sm text-text">
                        Quantité: {item.quantity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 font-nunito hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant Card pour mobile
function SaleCard({ sale, onViewDetails }) {
  const client = getClientContact(sale);
  const clientName = getClientName(sale, "Client non enregistré");
  const displayName =
    clientName.length > 20 ? clientName.substring(0, 20) + "..." : clientName;

  return (
    <div className="bg-white border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
      {/* Ligne 1: Nom client + Méthode */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className="font-nunito font-medium text-sm truncate"
              title={clientName}
            >
              {displayName}
            </span>
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1 shrink-0">
              <BiShoppingBag size={12} />
              Vente
            </span>
          </div>
          <span
            className={`px-2 py-1 text-xs ${
              sale.method === "CASH"
                ? "bg-green-100 text-green-800"
                : sale.method === "MOBILE_MONEY"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-secondary-100 text-secondary-800"
            } shrink-0`}
          >
            {sale.method}
          </span>
        </div>

        {/* Ligne 2: Montant */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BiShoppingBag className="text-gray-400" size={16} />
            <span className="font-poppins font-bold text-base text-montant">
              {parseFloat(sale.amount).toLocaleString()} FCFA
            </span>
          </div>
        </div>
      </div>

      {/* Ligne 3: Services + Date + Contacts */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <BiShoppingBag size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {sale.services?.length || 0} service
                {sale.services?.length > 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <BiCalendar size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {new Date(sale.created_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {client?.email && (
              <a
                href={`mailto:${client.email}`}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaEnvelope size={14} />
              </a>
            )}
            {client?.telephone && (
              <a
                href={`tel:${client.telephone}`}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <BiPhone size={14} />
              </a>
            )}
            {client?.whatsapp && (
              <a
                href={getWhatsappLink(client)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 hover:text-green-700"
              >
                <FaWhatsapp size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Ligne 4: Bouton Détails pleine largeur */}
      <div className="p-3 bg-gray-50">
        <button
          onClick={() => onViewDetails(sale)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <BiDetail size={16} />
          Voir les détails
        </button>
      </div>
    </div>
  );
}

export default function SalesPage() {
  const {
    sales,
    isLoadingSales,
    getOnlySales,
    getSalesWithDateRange,
    createPayment,
    unpaidReservations,
    isLoadingUnpaidReservations,
    getUnpaidReservations,
    calculateReservationAmount,
  } = salesStore();

  const { services, loadServices } = useServiceStore();
  const { clients, getAllClient } = userManagementStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [searchTerm, setSearchTerm] = useState("");

  // États pour les services modifiables de la réservation
  const [reservationServices, setReservationServices] = useState([]);

  // États pour les filtres de période
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");

  // États pour les informations de la personne
  const [isForSomeoneElse, setIsForSomeoneElse] = useState(false);
  const [extraData, setExtraData] = useState({
    nom: "",
    prenom: "",
    email: "",
    tel: "",
  });

  // États pour la pagination et modal
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedSale, setSelectedSale] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Chargement initial
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      await getSalesWithDateRange();
      await loadServices();
      await getAllClient();
      await getUnpaidReservations();
    } catch (error) {
      console.error("Erreur lors du chargement initial:", error);
      toast.error("Erreur lors du chargement des données");
    }
  };

  // Charger les services de la réservation sélectionnée
  useEffect(() => {
    if (selectedReservation) {
      const reservation = unpaidReservations.find(
        (r) => r.id === parseInt(selectedReservation),
      );
      if (reservation && reservation.appointmentServices) {
        const editableServices = reservation.appointmentServices.map(
          (service) => ({
            ...service,
            quantity: service.quantity || service.quantity || 1,
            price: service.price || service.unitPrice || 0,
            name: service.name || service.nom || "Service",
            id: service.id,
          }),
        );
        setReservationServices(editableServices);
      }
    } else {
      setReservationServices([]);
    }
  }, [selectedReservation, unpaidReservations]);

  // Calculer le montant total de la réservation
  const calculateReservationTotal = () => {
    return reservationServices.reduce((total, service) => {
      return total + service.price * service.quantity;
    }, 0);
  };

  // Mettre à jour la quantité d'un service de réservation
  const updateReservationServicequantity = (index, quantity) => {
    setReservationServices((prev) =>
      prev.map((service, i) =>
        i === index ? { ...service, quantity: Math.max(1, quantity) } : service,
      ),
    );
  };

  // Fonction pour obtenir les dates d'une période (corrigée)
  const getPeriodDates = (period) => {
    const now = new Date();
    let start = new Date(now);
    let end = new Date(now);

    // Mettre fin à 23:59:59.999 pour inclure toute la journée
    end.setUTCDate(end.getUTCDate() + 1);
    end.setUTCHours(0, 0, 0, 0);
    // end.setUTCHours(23, 59, 59, 999);

    switch (period) {
      case "today":
        start.setUTCHours(0, 0, 0, 0);
        break;

      case "week":
        // Début de semaine (lundi)
        start.setUTCDate(
          now.getUTCDate() - now.getUTCDay() + (now.getUTCDay() === 0 ? -6 : 1),
        );
        start.setUTCHours(0, 0, 0, 0);
        // Fin de semaine (dimanche)
        end.setUTCDate(start.getUTCDate() + 7);
        end.setUTCHours(0, 0, 0, 0);
        break;

      case "month":
        // Début du mois
        start.setUTCDate(1);
        start.setUTCHours(0, 0, 0, 0);
        // Fin du mois (dernier jour)
        end = new Date(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0);
        end.setUTCHours(0, 0, 0, 0);
        break;

      default:
        // Pour "all" ou période personnalisée
        return { startDate: null, endDate: null };
    }

    // Formater pour l'input date (YYYY-MM-DD)
    const formatDate = (date) => {
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, "0");
      const d = String(date.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    // Formater pour l'API (ISO string)
    const formatDateISO = (date) => {
      return date.toISOString().split("T")[0]; // YYYY-MM-DD seulement
    };

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
      startDateISO: formatDateISO(start),
      endDateISO: formatDateISO(end),
    };
  };

  // Charger les ventes avec les filtres
  // const loadSales = async (
  //   period = periodFilter,
  //   customStartDate = startDate,
  //   customEndDate = endDate,
  // ) => {
  //   try {
  //     let start = customStartDate;
  //     let end = customEndDate;

  //     if (period !== "custom" && period !== "all") {
  //       const dates = getPeriodDates(period);
  //       console.log({ dates });
  //       start = dates.startDate;
  //       end = dates.endDate;
  //       setStartDate(dates.startDate);
  //       setEndDate(dates.endDate);
  //     }

  //     // if (period === "all" || !start || !end) {
  //     //   await getSalesWithDateRange();
  //     // } else {
  //     //   await getSalesWithDateRange(start, end);
  //     // }

  //     const now = new Date();
  //     switch (periodFilter) {
  //       case "today":
  //         const debut = new Date(now);
  //         debut.setUTCHours(0, 0, 0, 0);
  //         const fin = new Date(now);
  //         fin.setUTCDate(fin.getUTCDate() + 1);
  //         fin.setUTCHours(0, 0, 0, 0);
  //         await getSalesWithDateRange(debut.toISOString(), fin.toISOString());
  //         return;
  //       case "week":
  //         const weekStart = new Date(now);
  //         weekStart.setUTCDate(weekStart.getUTCDate() - 6);
  //         weekStart.setUTCHours(0, 0, 0, 0);
  //         const weekEnd = new Date(now);
  //         weekEnd.setUTCDate(weekEnd.getUTCDate());
  //         weekEnd.setUTCHours(0, 0, 0, 0);
  //         await getSalesWithDateRange(
  //           weekStart.toISOString(),
  //           weekEnd.toISOString(),
  //         );
  //         return;
  //       case "month":
  //         const monthStart = new Date(now);
  //         monthStart.setUTCDate(1);
  //         monthStart.setUTCHours(0, 0, 0, 0);
  //         const monthEnd = new Date(now);
  //         monthEnd.setUTCDate(28);
  //         monthEnd.setUTCHours(0, 0, 0, 0);
  //         await getSalesWithDateRange(
  //           monthStart.toISOString(),
  //           monthEnd.toISOString(),
  //         );
  //         return;
  //       case "all":
  //         await getSalesWithDateRange();
  //         return;
  //       default:
  //         await getSalesWithDateRange();
  //         return;
  //     }

  //     // Réinitialiser la pagination après un nouveau chargement
  //     setCurrentPage(1);
  //   } catch (error) {
  //     console.error("Erreur lors du chargement des ventes:", error);
  //     toast.error("Erreur lors du chargement des ventes");
  //   }
  // };
  const loadSales = async (
    period = periodFilter,
    customStartDate = startDate,
    customEndDate = endDate,
  ) => {
    try {
      let start = null;
      let end = null;

      if (period === "custom") {
        // Période personnalisée : utiliser les dates fournies
        if (customStartDate && customEndDate) {
          start = customStartDate;
          end = customEndDate;
        }
      } else if (period !== "all") {
        // Période prédéfinie : calculer les dates
        const dates = getPeriodDates(period);
        start = dates.startDateISO;
        end = dates.endDateISO;

        // Mettre à jour les inputs date pour l'affichage
        setStartDate(dates.startDate);
        setEndDate(dates.endDate);
      }

      // Appeler l'API avec les paramètres appropriés
      if (period === "all" || (!start && !end)) {
        // Pas de filtre de date
        await getSalesWithDateRange();
      } else if (start && end) {
        // Appeler avec les dates formatées
        await getSalesWithDateRange(start, end);
      } else {
        // Cas par défaut : pas de filtre
        await getSalesWithDateRange();
      }

      // Réinitialiser la pagination après un nouveau chargement
      setCurrentPage(1);
    } catch (error) {
      console.error("Erreur lors du chargement des ventes:", error);
      toast.error("Erreur lors du chargement des ventes");
    }
  };

  // const handlePeriodChange = async (period) => {
  //   setPeriodFilter(period);

  //   if (period === "all") {
  //     setStartDate("");
  //     setEndDate("");
  //     await loadSales("all", "", "");
  //   } else if (period !== "custom") {
  //     const dates = getPeriodDates(period);
  //     await loadSales(period, dates.startDate, dates.endDate);
  //   }
  // };

  // Effet pour les dates personnalisées

  const handlePeriodChange = async (period) => {
    setPeriodFilter(period);

    if (period === "all") {
      setStartDate("");
      setEndDate("");
      await loadSales("all", "", "");
    } else if (period === "custom") {
      // Pour "custom", on attend que l'utilisateur sélectionne les dates
      // Ne pas charger tout de suite
      setStartDate("");
      setEndDate("");
    } else {
      // Pour les périodes prédéfinies, charger immédiatement
      await loadSales(period);
    }
  };

  // useEffect(() => {
  //   if (periodFilter === "custom" && startDate && endDate) {
  //     const timer = setTimeout(() => {
  //       loadSales("custom", startDate, endDate);
  //     }, 500);
  //     return () => clearTimeout(timer);
  //   }
  // }, [startDate, endDate, periodFilter]);

  useEffect(() => {
    if (periodFilter === "custom" && startDate && endDate) {
      // Convertir les dates d'affichage (YYYY-MM-DD) en format pour l'API
      const startISO = startDate; // Déjà en YYYY-MM-DD
      const endISO = endDate; // Déjà en YYYY-MM-DD

      const timer = setTimeout(() => {
        loadSales("custom", startISO, endISO);
      }, 500); // Délai pour éviter trop d'appels API

      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, periodFilter]);

  const resetFilters = async () => {
    setPeriodFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    await loadSales("all", "", "");
  };

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case "today":
        return "Aujourd'hui";
      case "week":
        return "Cette semaine";
      case "month":
        return "Ce mois";
      case "custom":
        return "Période personnalisée";
      default:
        return "Toutes les périodes";
    }
  };

  // Fonctions pour la création de vente normale
  const handleAddService = (serviceId) => {
    if (!serviceId) return;

    const service = services.find((s) => s.id === parseInt(serviceId));
    if (service) {
      const existingService = selectedServices.find(
        (s) => s.serviceId === serviceId,
      );
      if (existingService) {
        const updatedServices = selectedServices.map((s) =>
          s.serviceId === serviceId
            ? { ...s, quantity: s.quantity ? s.quantity + 1 : 1 }
            : s,
        );
        setSelectedServices(updatedServices);
      } else {
        setSelectedServices([
          ...selectedServices,
          {
            serviceId: serviceId,
            quantity: 1,
            nom: service.nom,
            price: service.price,
          },
        ]);
      }
    }
  };

  const handleRemoveService = (index) => {
    setSelectedServices((prev) => prev.filter((_, i) => i !== index));
  };

  const updateServicequantity = (index, quantity) => {
    setSelectedServices((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, quantity: Math.max(1, quantity) } : item,
      ),
    );
  };

  const calculateTotal = () => {
    return selectedServices.reduce((total, service) => {
      return total + service.price * service.quantity;
    }, 0);
  };

  const handleExtraDataChange = (field, value) => {
    setExtraData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setSelectedServices([]);
    setSelectedClient("");
    setSelectedReservation("");
    setReservationServices([]);
    setPaymentMethod("CASH");
    setIsForSomeoneElse(false);
    setExtraData({
      nom: "",
      prenom: "",
      email: "",
      tel: "",
    });
  };

  const handleCreateSale = async () => {
    if (selectedServices.length === 0) {
      toast.error("Veuillez sélectionner au moins un service");
      return;
    }

    if (isForSomeoneElse) {
      if (!extraData.nom.trim() || !extraData.prenom.trim()) {
        toast.error("Veuillez renseigner le nom et prénom de la personne");
        return;
      }
      if (!extraData.tel.trim()) {
        toast.error("Veuillez renseigner le numéro de téléphone");
        return;
      }
    }

    const paymentData = {
      amount: calculateTotal(),
      method: paymentMethod,
      client_id: selectedClient,
      services: selectedServices.map((service) => ({
        id: service.serviceId,
        quantity: service.quantity,
      })),
    };

    if (isForSomeoneElse) {
      paymentData.extraData = {
        nom: extraData.nom.trim(),
        prenom: extraData.prenom.trim(),
        email: extraData.email.trim() || null,
        tel: extraData.tel.trim(),
      };
    }

    const result = await createPayment(paymentData);
    if (result) {
      setShowCreateModal(false);
      resetForm();
      await loadInitialData();
      toast.success("Vente enregistrée avec succès!");
    }
  };

  // Fonction pour le règlement de réservation
  const handleReservationPayment = async () => {
    if (!selectedReservation) {
      toast.error("Veuillez sélectionner une réservation");
      return;
    }

    const reservation = unpaidReservations.find(
      (r) => r.id === parseInt(selectedReservation),
    );
    if (!reservation) {
      toast.error("Réservation non trouvée");
      return;
    }

    const paymentData = {
      method: paymentMethod,
      amount: calculateReservationTotal(),
      appointmentId: selectedReservation,
      services: selectedServices.map((service) => ({
        id: service.id,
        quantity: service.quantity,
      })),
    };

    const result = await createPayment(paymentData);
    if (result) {
      setShowReservationModal(false);
      resetForm();
      await loadInitialData();
      toast.success("Réservation réglée avec succès!");
    }
  };

  // Fonctions pour le tableau responsive
  const handleViewDetails = (sale) => {
    setSelectedSale(sale);
    setIsModalOpen(true);
  };

  // Filtrage local (recherche)
  const filteredSales = useMemo(() => {
    if (!sales) return [];

    return sales.filter((sale) => {
      if (!searchTerm) return true;


      const servicesText =
        sale.services
          ?.map((s) => s.service?.nom || "")
          .join(" ")
          .toLowerCase() || "";

      const searchLower = searchTerm.toLowerCase();

      return (
        matchesClientSearch(sale, searchTerm) ||
        servicesText.includes(searchLower) ||
        sale.amount.toString().includes(searchTerm) ||
        sale.code?.toLowerCase().includes(searchLower) ||
        sale.method?.toLowerCase().includes(searchLower)
      );
    });
  }, [sales, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
  const paginatedSales = filteredSales.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Colonnes pour le tableau desktop
  const columns = [
    {
      headerName: "Client",
      width: "25%",
      render: (sale) => {
        const client = getClientContact(sale);
        const clientName = getClientName(sale, "Client non enregistré");
        const displayName =
          clientName.length > 25
            ? clientName.substring(0, 25) + "..."
            : clientName;

        return (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="font-nunito font-normal text-sm truncate"
                title={clientName}
              >
                {displayName}
              </span>
              <div className="flex gap-1 shrink-0">
                {client?.email && (
                  <a
                    href={`mailto:${client.email}`}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaEnvelope size={12} />
                  </a>
                )}
                {client?.telephone && (
                  <a
                    href={`tel:${client.telephone}`}
                    className="text-secondary-500 hover:text-secondary-700"
                  >
                    <BiPhone size={12} />
                  </a>
                )}
                {client?.whatsapp && (
                  <a
                    href={getWhatsappLink(client)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-500 hover:text-green-700"
                  >
                    <FaWhatsapp size={12} />
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Type / Méthode",
      width: "20%",
      render: (sale) => {
        return (
          <div className="flex flex-col gap-1">
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 w-fit">
              Vente
            </span>
            <span
              className={`px-2 py-1 text-xs ${
                sale.method === "CASH"
                  ? "bg-green-100 text-green-800"
                  : sale.method === "MOBILE_MONEY"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-secondary-100 text-secondary-800"
              } w-fit`}
            >
              {sale.method}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Montant",
      width: "15%",
      render: (sale) => (
        <div className="text-sm text-montant">
          {parseFloat(sale.amount).toLocaleString()} FCFA
        </div>
      ),
    },
    {
      headerName: "Services",
      width: "10%",
      render: (sale) => (
        <div className="flex items-center gap-1">
          <BiShoppingBag size={14} className="text-gray-500" />
          <span className="font-nunito text-xs">
            {sale.services?.length || 0}
          </span>
        </div>
      ),
    },
    {
      headerName: "Date",
      width: "15%",
      render: (sale) => (
        <div className="font-nunito text-xs text-text">
          {new Date(sale.created_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </div>
      ),
    },
    {
      headerName: "",
      width: "15%",
      render: (sale) => (
        <button
          onClick={() => handleViewDetails(sale)}
          className="w-full px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
        >
          <BiDetail size={14} />
          Détails
        </button>
      ),
    },
  ];

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
        <div>
          <h1 className="font-poppins font-semibold text-[18px] text-title">
            Gestion des Ventes
          </h1>
          <p className="font-nunito text-[14px] text-text mt-1">
            Créez et gérez les ventes enregistrées en physique
          </p>
        </div>

        <div className="flex flex-col gap-4 mt-4 lg:mt-0 md:flex-row md:gap-2">
          <button
            onClick={() => setShowReservationModal(true)}
            className="items-center justify-center gap-2 px-4 py-2 bg-black text-white font-medium hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex space-x-3 duration-200 hover:shadow-xl font-nunito text-[14px]"
            disabled={
              isLoadingUnpaidReservations || unpaidReservations.length === 0
            }
          >
            <BiReceipt size={20} />
            Règler Réservation
            {unpaidReservations.length > 0 && (
              <span className="bg-red-500 text-white font-nunito text-[14px] px-2 py-1">
                {unpaidReservations.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-secondary-600 text-white hover:bg-secondary-700 transition-colors font-nunito text-[14px] font-semibold"
          >
            <BiPlus size={20} />
            Nouvelle Vente
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white p-4 shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col 2xl:flex-row gap-4">
          {/* Barre de recherche */}
          <div className="flex-1 relative">
            <BiSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Rechercher par client, service ou montant..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            />
          </div>

          {/* Filtres de période et statut */}
          <div className="flex flex-col xl:flex-row justify-between gap-3">
            {/* Sélecteur de période rapide */}
            <select
              value={periodFilter}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            >
              <option value="all">Toutes les périodes</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="custom">Période personnalisée</option>
            </select>

            {/* Dates personnalisées */}
            {periodFilter === "custom" && (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex flex-1 justify-end items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                  <BiCalendar className="text-gray-400" size={16} />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="focus:outline-none font-nunito text-[14px] w-32"
                  />
                </div>
                <div className="flex flex-1 justify-end items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                  <BiCalendar className="text-gray-400" size={16} />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="focus:outline-none font-nunito text-[14px] w-32"
                  />
                </div>
              </div>
            )}

            {/* Bouton réinitialiser */}
            {(periodFilter !== "all" || searchTerm) && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-3 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                <BiReset size={16} />
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Indicateur de période active */}
        {(periodFilter !== "all" || searchTerm) && (
          <div className="mt-3 flex items-center gap-2 font-nunito text-[14px]">
            <span className="text-gray-600">Filtres actifs:</span>
            {periodFilter !== "all" && (
              <span className="bg-secondary-100 text-secondary-800 px-2 py-1 font-nunito text-[14px]">
                {getPeriodLabel()}
              </span>
            )}
            {searchTerm && (
              <span className="bg-green-100 text-green-800 px-2 py-1 font-nunito text-[14px]">
                Recherche: "{searchTerm}"
              </span>
            )}
            <span className="bg-gray-100 text-gray-800 px-2 py-1 font-nunito text-[14px]">
              {filteredSales.length} résultat
              {filteredSales.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {isLoadingSales ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600"></div>
        </div>
      ) : (
        <>
          {/* Pagination en haut */}
          {totalPages > 1 && filteredSales.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} • {filteredSales.length}{" "}
                élément{filteredSales.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Tableau Desktop */}
          <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left font-poppins font-bold text-xs text-title border-b border-gray-200"
                        style={{ width: column.width }}
                      >
                        {column.headerName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y font-poppins divide-gray-200">
                  {paginatedSales.map((sale, index) => (
                    <tr
                      key={sale.id}
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className="px-4 py-3">
                          <div className="flex items-center h-full">
                            {column.render(sale)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cartes Mobile */}
          <div className="lg:hidden space-y-3 mb-6">
            {paginatedSales.map((sale) => (
              <SaleCard
                key={sale.id}
                sale={sale}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Pagination en bas */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} • {filteredSales.length}{" "}
                élément{filteredSales.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Message vide */}
          {filteredSales.length === 0 && (
            <div className="text-center py-12 bg-white border border-gray-200">
              <BiShoppingBag className="mx-auto text-gray-300" size={40} />
              <p className="font-nunito text-sm text-text mt-3">
                {searchTerm
                  ? "Aucun résultat trouvé"
                  : "Aucune vente pour cette période"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Modal de détails */}
      <PaymentDetailModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSale(null);
        }}
        payment={selectedSale}
      />

      {/* Modal de création de vente normale (inchangé) */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-2 md:p-6 border-b border-gray-200">
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                Nouvelle Vente
              </h2>
            </div>

            <div className="p-2 md:p-6">
              {/* Option pour quelqu'un d'autre */}
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isForSomeoneElse}
                    onChange={(e) => setIsForSomeoneElse(e.target.checked)}
                    className="border-gray-300 text-secondary-600 focus:ring-secondary-500"
                  />
                  <span className="font-poppins font-medium text-[15px] text-subtitle">
                    Cette vente est pour quelqu'un d'autre
                  </span>
                </label>
              </div>

              {/* Sélection du client OU informations de la personne */}
              {!isForSomeoneElse ? (
                <div className="mb-6">
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                    Client (optionnel)
                  </label>
                  <select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                  >
                    <option value="">Sélectionner un client existant</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.prenom} {client.nom} - {client.telephone}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BiUser className="text-secondary-600" size={20} />
                    <h3 className="font-poppins font-semibold text-[18px] text-title">
                      Informations de la personne
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        value={extraData.prenom}
                        onChange={(e) =>
                          handleExtraDataChange("prenom", e.target.value)
                        }
                        className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                        placeholder="Prénom"
                      />
                    </div>
                    <div>
                      <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        value={extraData.nom}
                        onChange={(e) =>
                          handleExtraDataChange("nom", e.target.value)
                        }
                        className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                        placeholder="Nom"
                      />
                    </div>
                    <div>
                      <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        value={extraData.tel}
                        onChange={(e) =>
                          handleExtraDataChange("tel", e.target.value)
                        }
                        className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                        placeholder="Numéro de téléphone"
                      />
                    </div>
                    <div>
                      <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
                        Email (optionnel)
                      </label>
                      <input
                        type="email"
                        value={extraData.email}
                        onChange={(e) =>
                          handleExtraDataChange("email", e.target.value)
                        }
                        className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                        placeholder="Email"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Ajout de services */}
              <div className="mb-6">
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                  Ajouter un service
                </label>
                <div className="flex gap-2">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddService(e.target.value);
                        e.target.value = "";
                      }
                    }}
                    className="flex-1 border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                  >
                    <option value="">Sélectionner un service</option>
                    {services
                      .filter((s) => s.is_active)
                      .map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.nom} - {service.price.toLocaleString()} FCFA
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Liste des services sélectionnés */}
              {selectedServices.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-poppins font-semibold text-[18px] text-title mb-3">
                    Services sélectionnés
                  </h3>
                  <div className="space-y-3">
                    {selectedServices.map((service, index) => {
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border border-gray-200"
                        >
                          <div className="flex-1">
                            <p className="font-poppins font-semibold text-[18px] text-title">
                              {service.nom}
                            </p>
                            <p className="font-nunito text-[14px] text-text">
                              {service.price.toLocaleString()} FCFA ×{" "}
                              {service.quantity} ={" "}
                              {(
                                service.price * service.quantity
                              ).toLocaleString()}{" "}
                              FCFA
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <label className="font-nunito text-[14px] text-text">
                                Quantité:
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={service.quantity}
                                onChange={(e) =>
                                  updateServicequantity(
                                    index,
                                    parseInt(e.target.value) || 1,
                                  )
                                }
                                className="w-20 border border-gray-300 px-2 py-1 text-center font-nunito text-[14px]"
                              />
                            </div>
                            <button
                              onClick={() => handleRemoveService(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Total et méthode de paiement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                    Méthode de paiement
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                  >
                    <option value="CASH">Espèces</option>
                    <option value="MOBILE_MONEY">Mobile Money</option>
                  </select>
                </div>

                <div className="bg-gray-50 p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-poppins font-semibold text-[18px] text-title">
                      Total:
                    </span>
                    <span className="font-poppins font-bold text-[16px] text-montant">
                      {calculateTotal().toLocaleString()} FCFA
                    </span>
                  </div>
                  <p className="font-nunito text-[14px] text-text">
                    {selectedServices.length} service
                    {selectedServices.length !== 1 ? "s" : ""} sélectionné
                    {selectedServices.length !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-2 md:p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateSale}
                disabled={selectedServices.length === 0}
                className="px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Enregistrer la vente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de règlement de réservation (inchangé) */}
      {showReservationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-2 md:p-6 border-b border-gray-200">
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                Règlement de Réservation
              </h2>
            </div>

            <div className="p-2 md:p-6">
              {isLoadingUnpaidReservations ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                </div>
              ) : unpaidReservations.length === 0 ? (
                <div className="text-center py-8">
                  <BiReceipt className="mx-auto text-gray-400" size={48} />
                  <p className="font-nunito text-[14px] text-text mt-4">
                    Aucune réservation impayée
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                      Sélectionner une réservation impayée
                    </label>
                    <select
                      value={selectedReservation}
                      onChange={(e) => setSelectedReservation(e.target.value)}
                      className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                    >
                      <option value="">Choisir une réservation</option>
                      {unpaidReservations.map((reservation) => (
                        <option key={reservation.id} value={reservation.id}>
                          Réservation #{reservation.id} -{" "}
                          {getClientName(reservation)}{" "}
                          -{" "}
                          {calculateReservationAmount(
                            reservation,
                          ).toLocaleString()}{" "}
                          FCFA
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedReservation && (
                    <>
                      {/* Informations de la réservation */}
                      <div className="mb-6 p-4 bg-secondary-50">
                        <h3 className="font-poppins font-semibold text-[18px] text-title mb-2">
                          Informations de la réservation
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-nunito text-[14px]">
                          <div>
                            <span className="font-poppins font-medium text-[15px] text-subtitle">
                              Client:
                            </span>{" "}
                            {getClientName(
                              unpaidReservations.find(
                                (r) => r.id === parseInt(selectedReservation),
                              ),
                            )}
                          </div>
                          <div>
                            <span className="font-poppins font-medium text-[15px] text-subtitle">
                              Téléphone:
                            </span>{" "}
                            {getClientContact(
                              unpaidReservations.find(
                                (r) => r.id === parseInt(selectedReservation),
                              ),
                            )?.telephone || "Non renseigné"}
                          </div>
                          <div>
                            <span className="font-poppins font-medium text-[15px] text-subtitle">
                              Date réservation:
                            </span>{" "}
                            {new Date(
                              unpaidReservations.find(
                                (r) => r.id === parseInt(selectedReservation),
                              )?.created_at,
                            ).toLocaleDateString("fr-FR")}
                          </div>
                          <div>
                            <span className="font-poppins font-medium text-[15px] text-subtitle">
                              Statut:
                            </span>{" "}
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 font-nunito text-[14px]">
                              En attente de paiement
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Services de la réservation avec quantités modifiables */}
                      <div className="mb-6">
                        <h3 className="font-poppins font-semibold text-[18px] text-title mb-3">
                          Services de la réservation
                        </h3>
                        <div className="space-y-3">
                          {reservationServices.map((service, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 border border-gray-200"
                            >
                              <div className="flex-1">
                                <p className="font-poppins font-semibold text-[18px] text-title">
                                  {service.name}
                                </p>
                                <p className="font-nunito text-[14px] text-text">
                                  {service.price.toLocaleString()} FCFA ×{" "}
                                  {service.quantity} ={" "}
                                  {(
                                    service.price * service.quantity
                                  ).toLocaleString()}{" "}
                                  FCFA
                                </p>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <label className="font-nunito text-[14px] text-text">
                                    Quantité:
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={service.quantity}
                                    disabled={true}
                                    onChange={(e) =>
                                      updateReservationServicequantity(
                                        index,
                                        parseInt(e.target.value) || 1,
                                      )
                                    }
                                    className="w-20 border border-gray-300 px-2 py-1 text-center font-nunito text-[14px]"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Méthode de paiement et total */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                            Méthode de paiement
                          </label>
                          <select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
                          >
                            <option value="CASH">Espèces</option>
                            <option value="MOBILE_MONEY">Mobile Money</option>
                          </select>
                        </div>

                        <div className="bg-gray-50 p-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-poppins font-semibold text-[18px] text-title">
                              Montant à régler:
                            </span>
                            <span className="font-poppins font-bold text-[16px] text-montant">
                              {calculateReservationTotal().toLocaleString()}{" "}
                              FCFA
                            </span>
                          </div>
                          <p className="font-nunito text-[14px] text-text">
                            {reservationServices.length} service
                            {reservationServices.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="p-2 md:p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReservationModal(false);
                  resetForm();
                }}
                className="px-4 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReservationPayment}
                disabled={
                  !selectedReservation ||
                  isLoadingUnpaidReservations ||
                  reservationServices.length === 0
                }
                className="px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Régler la réservation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
